import React, {createContext, useContext, useEffect, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {useAuthUser} from '../../@crema/utility/AuthHooks';
import {Quote} from '../quotes/QuotesProvider';
import {Symbol} from '../symbols/SymbolProvider';

export type SymbolQuoteValue = {
  symbol: string;
  symbolId: string;
};

export type SymbolQuote = {
  id: string;
};

export type SingleSymbolQuoteValue = {
  id: number;
};

type WatchlistContextType = {
  symbols: Symbol[];
  symbolQuotes: SymbolQuote[];
  symbolQuoteValues: SymbolQuoteValue[];
  singleSymbolQuoteValues: SingleSymbolQuoteValue[];
  quotes: Quote[];
  loading: boolean;
};

interface WatchlistActionsContextType {
  createSymbolQuote: Function;
  getSymbolQuoteValuesForSymbol: Function;
  removeSymbolQuote: Function;
}

const defaultWatchlistContext = {
  symbols: [],
  symbolQuoteValues: [],
  symbolQuotes: [],
  singleSymbolQuoteValues: [],
  quotes: [],
  loading: false,
};

const WatchlistContext = createContext<WatchlistContextType>(
  defaultWatchlistContext,
);

const WatchlistActionsContext = createContext<WatchlistActionsContextType>({
  createSymbolQuote: () => {},
  getSymbolQuoteValuesForSymbol: () => {},
  removeSymbolQuote: () => {},
});

export const useWatchlistContext = () => useContext(WatchlistContext);
export const useWatchlistActionsContext = () =>
  useContext(WatchlistActionsContext);

const WatchlistProvider = ({children}: any) => {
  const {user} = useAuthUser();
  const [symbols, setSymbols] = useState(defaultWatchlistContext.symbols);
  const [symbolQuotes, setSymbolQuotes] = useState(
    defaultWatchlistContext.symbolQuotes,
  );
  const [singleSymbolQuoteValues, setSingleSymbolQuoteValues] = useState(
    defaultWatchlistContext.singleSymbolQuoteValues,
  );
  const [symbolQuoteValues, setSymbolQuoteValues] = useState(
    defaultWatchlistContext.symbolQuoteValues,
  );
  const [quotes, setQuotes] = useState(defaultWatchlistContext.quotes);
  const [loading, setLoading] = useState(defaultWatchlistContext.loading);

  useEffect(() => {
    fetchSymbolsAndQuotes();
  }, []);

  const fetchSymbolsAndQuotes = () => {
    setLoading(true);
    Promise.all([
      getSymbols().then(({data}) => setSymbols(data.data)),
      getSymbolQuotes().then(({data}) => setSymbolQuotes(data.data)),
      getSymbolQuoteValues().then(({data}) => setSymbolQuoteValues(data.data)),
      getQuotes().then(({data}) => setQuotes(data.data)),
    ])
      .then(() => {
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const createSymbolQuote = (symbolId: string, quoteId: string) => {
    createSymbolQuoteRequest(symbolId, quoteId).then(() => {
      fetchSymbolsAndQuotes();
    });
  };

  const getSymbols = async () => {
    return jwtAxios.get(`/symbol/get-all/${user.id}`);
  };

  const getSymbolQuoteValues = async () => {
    return jwtAxios.get(`/symbol-quote-value/get-all/${user.id}`);
  };

  const getSymbolQuotes = async () => {
    return jwtAxios.get(`/symbol-quote/get-all-native/${user.id}`);
  };

  const removeSymbolQuote = (id: string) => {
    setLoading(true);
    deleteSymbolQuote(id).then(() => {
      getSymbolQuotes().then(({data}) => {
        setSymbolQuotes(data.data);
        setLoading(false);
      });
    });
  };

  const deleteSymbolQuote = async (id: string) => {
    return jwtAxios.delete(`/symbol-quote/remove/${id}`);
  };

  const getSymbolQuoteValuesForSymbol = async (symbolId: string) => {
    jwtAxios
      .get(`/symbol-quote-value/symbol/get-all/${symbolId}`)
      .then(({data}) => setSingleSymbolQuoteValues(data.data));
  };

  const getQuotes = async () => {
    return jwtAxios.get(`/quote/get-all/${user.id}`);
  };

  const createSymbolQuoteRequest = async (
    symbolId: string,
    quoteId: string,
  ) => {
    return jwtAxios.post(`/symbol-quote/create/${user.id}`, {
      symbolId,
      quoteId,
    });
  };

  return (
    <WatchlistContext.Provider
      value={{
        symbols,
        symbolQuotes,
        quotes,
        symbolQuoteValues,
        singleSymbolQuoteValues,
        loading,
      }}>
      <WatchlistActionsContext.Provider
        value={{
          createSymbolQuote,
          getSymbolQuoteValuesForSymbol,
          removeSymbolQuote,
        }}>
        {children}
      </WatchlistActionsContext.Provider>
    </WatchlistContext.Provider>
  );
};

export default WatchlistProvider;
